/**
 * The review step's derived state: what we start from, what we ask about,
 * and what still stands between the user and a valid payload. Every verdict
 * is an `import:` key so web and mobile render the same words.
 */
import { PAYMENT_OVERRIDE, importableRows } from './buildImportPayload';
import type { ImportReviewState } from './buildImportPayload';
import { MAX_IMPORT_MEMBERS, defaultGroupName } from './importFile';
import { normalizeLabel } from './labelResolution';
import type { LabelResolutionSource } from './labelResolution';
import {
  formatTaxonomyPath,
  mapSplitwiseCategory,
  parseTaxonomyPath,
} from './splitwiseCategories';
import type { SplitwiseFile, SplitwiseRow } from './splitwiseCsv';

export interface InitialReviewState {
  review: ImportReviewState;
  /** The file's own currency is not one we support, so the device one stands in. */
  currencyFallback: boolean;
}

/**
 * `isSupportedCurrency` is a parameter rather than an import: each platform
 * has its own `@splitfiesta/i18n` entry, and pulling one of them in here
 * would evaluate the other platform's singleton inside a shared module.
 */
export function initialReviewState(
  file: SplitwiseFile,
  fileName: string,
  deviceCurrency: string,
  isSupportedCurrency: (code: string) => boolean
): InitialReviewState {
  // `currencies` is most-frequent-first, which is the sensible default for
  // a group whose rows mix currencies.
  const fileCurrency = file.currencies[0];
  const usable =
    fileCurrency !== undefined && isSupportedCurrency(fileCurrency);

  return {
    review: {
      groupName: defaultGroupName(fileName),
      groupCurrency: usable ? fileCurrency : deviceCurrency,
      // The file says nothing about what kind of group this is; most
      // Splitwise groups are friends, and the review lets the user change it.
      groupCategory: 'friends',
      // -1 means "nothing picked"; the step stays blocked until it is a column.
      selfColumn: -1,
      memberNames: [...file.members],
      categoryOverrides: {},
      zeroRowAssignments: {},
    },
    currencyFallback: fileCurrency !== undefined && !usable,
  };
}

export interface ImportCategoryRow {
  /** The override slot: `buildImportPayload` reads `categoryOverrides[key]`. */
  key: string;
  /** The label as the file first spelled it. */
  label: string;
  rows: number;
  /** `PAYMENT_OVERRIDE` or a taxonomy path. */
  mapped: string;
  /** Nothing resolved the label, so it files under Other until the user picks. */
  unknown: boolean;
  source: LabelResolutionSource | null;
}

/** Every label, payments included, since the review may flip either way. */
export function distinctCategories(file: SplitwiseFile): ImportCategoryRow[] {
  // Grouped by the parser's normalised label, so "Groceries" and "groceries "
  // share one override slot and are not offered as two independent choices.
  const seen = new Map<string, { label: string; rows: number }>();
  for (const row of file.rows) {
    const key = normalizeLabel(row.category);
    const entry = seen.get(key);
    if (entry) entry.rows += 1;
    else seen.set(key, { label: row.category, rows: 1 });
  }

  return [...seen.entries()].map(([key, { label, rows }]) => {
    const resolution = Object.hasOwn(file.resolutions, key)
      ? file.resolutions[key]
      : undefined;
    let mapped = formatTaxonomyPath(mapSplitwiseCategory(key));
    if (resolution?.kind === 'payment') mapped = PAYMENT_OVERRIDE;
    else if (resolution?.kind === 'category')
      mapped = formatTaxonomyPath(parseTaxonomyPath(resolution.path));
    return {
      key,
      label,
      rows,
      mapped,
      unknown: resolution === undefined,
      source: resolution?.source ?? null,
    };
  });
}

/** The rows the "no balance effect" section asks about, in file order. */
export function zeroEffectRows(file: SplitwiseFile): SplitwiseRow[] {
  return file.rows.filter(row => row.skipReason === 'no-balance-effect');
}

/** Assigns one zero-effect row to a member column, or leaves it out (`null`). */
export function assignZeroRow(
  review: ImportReviewState,
  rowIndex: number,
  column: number | null
): ImportReviewState {
  const zeroRowAssignments = { ...review.zeroRowAssignments };
  if (column === null) delete zeroRowAssignments[rowIndex];
  else zeroRowAssignments[rowIndex] = column;
  return { ...review, zeroRowAssignments };
}

/** The bulk control: every zero-effect row to one member, or none of them. */
export function assignAllZeroRows(
  review: ImportReviewState,
  file: SplitwiseFile,
  column: number | null
): ImportReviewState {
  if (column === null) return { ...review, zeroRowAssignments: {} };
  const zeroRowAssignments: Record<number, number> = {};
  for (const row of zeroEffectRows(file))
    zeroRowAssignments[row.rowIndex] = column;
  return { ...review, zeroRowAssignments };
}

/**
 * What the bulk control displays: the column when every zero-effect row is
 * that member's, `null` when none is assigned, `'mixed'` when the rows
 * disagree — a state the control shows but cannot be set to. Takes the rows
 * both screens already hold rather than re-deriving them every render.
 */
export function zeroRowsBulkChoice(
  review: ImportReviewState,
  rows: SplitwiseRow[]
): number | null | 'mixed' {
  const columns = rows.map(row => review.zeroRowAssignments[row.rowIndex]);
  const [first] = columns;
  if (columns.every(column => column === undefined)) return null;
  return first !== undefined && columns.every(column => column === first)
    ? first
    : 'mixed';
}

export function reviewBlockers(
  review: ImportReviewState,
  file: SplitwiseFile
): string[] {
  const blockers: string[] = [];

  if (review.selfColumn < 0 || review.selfColumn >= review.memberNames.length) {
    blockers.push('import:errors.pickSelf');
  }
  if (!review.groupName.trim()) {
    blockers.push('import:errors.groupNameRequired');
  }

  const names = review.memberNames.map(name => name.trim());
  // Name edits are positional, so a list that no longer lines up with the
  // file's columns cannot be repaired by renaming anyone.
  if (names.length !== file.members.length) {
    blockers.push('import:errors.memberCountMismatch');
  }
  if (names.some(name => name === '')) {
    blockers.push('import:errors.emptyMemberName');
  }
  const lowered = names
    .filter(name => name !== '')
    .map(name => name.toLowerCase());
  if (new Set(lowered).size !== lowered.length) {
    blockers.push('import:errors.duplicateMemberName');
  }
  if (file.members.length > MAX_IMPORT_MEMBERS) {
    blockers.push('import:errors.tooManyMembers');
  }
  // A file whose every row was skipped (or whose payments found no expense to
  // settle) would post an empty import and create an empty group.
  if (
    importableRows(file, review.zeroRowAssignments, review.categoryOverrides)
      .length === 0
  ) {
    blockers.push('import:errors.nothingToImport');
  }

  return blockers;
}
